import React from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { parseISO } from "date-fns";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemAvatar,
  ListItemText,
  Badge,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  Typography,
  Tooltip,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import SupportIcon from "@mui/icons-material/Support";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import DeleteIcon from "@mui/icons-material/Delete";
import InboxIcon from "@mui/icons-material/Inbox";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";

import { useGlobalState } from "../state";
import SteuertoolsLogo from "../assets/steuertools_logo_inverted.svg";
import { supabase } from "../config/supabaseClient";
import { getSsoBaseUrl } from "../config/wordpress";
import { Notification } from "../types";

const Header = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [session] = useGlobalState("session");
  const [user] = useGlobalState("user");
  const ssoBaseUrl = getSsoBaseUrl(session?.access_token ?? "");
  const avatarUrl = user?.user_metadata.avatar_url;

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const [notifications, setNotifications] = useGlobalState("notifications");
  const [notificationsCount, setNotificationsCount] = React.useState<number>(0);
  const [showNotificationsDialog, setShowNotificationsDialog] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    const getUnreadNotificationsCount = (notifications: Notification[]) => {
      return notifications.filter((obj) => {
        return obj.unread === true;
      }).length;
    };

    setNotificationsCount(getUnreadNotificationsCount(notifications));
  }, [notifications]);

  React.useEffect(() => {
    if (user) {
      supabase
        .from("appNotifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .then(({ data, error }) => {
          if (error) {
            enqueueSnackbar("Fehler: " + error.message, {
              variant: "error",
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
            });
          }

          if (data) {
            setNotifications(data);
          }
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  React.useEffect(() => {
    const appNotifications = supabase
      .channel("custom-all-channel")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "appNotifications" },
        (payload) => {
          if (payload.eventType === "INSERT") {
            const newNotification: Notification | any = payload.new;

            setNotifications((notifications) => [
              newNotification,
              ...notifications,
            ]);
          }

          if (payload.eventType === "UPDATE") {
            const idToUpdate = payload.old.id;
            const updatedNotification: Notification | any = payload.new;
            const notificationsCandidate: Notification[] = notifications.map(
              (obj) => {
                if (obj.id === idToUpdate) {
                  return updatedNotification;
                } else {
                  return obj;
                }
              }
            );

            setNotifications(notificationsCandidate);
          }

          if (payload.eventType === "DELETE") {
            const idToRemove = payload.old.id;
            const notificationsCandidate: Notification[] = notifications.filter(
              (obj) => obj.id !== idToRemove
            );

            setNotifications(notificationsCandidate);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(appNotifications);
    };
  }, [setNotifications, notifications]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    supabase.auth
      .signOut()
      .then(() => {
        setAnchorElUser(null);
        navigate("/login");
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <AppBar position="static" sx={{ boxShadow: "none" }}>
      <Container>
        <Toolbar disableGutters>
          <Box sx={{ display: "flex", pr: "20px" }}>
            <Button
              sx={{ my: 2, color: "white" }}
              onClick={() => navigate("/")}
            >
              <img
                src={SteuertoolsLogo}
                alt="steuertools.de Logo"
                style={{ width: "200px" }}
                onClick={() => navigate("/")}
              />
            </Button>
          </Box>
          <Box sx={{ flexGrow: 1 }}></Box>
          {session?.user && (
            <Box sx={{ flexGrow: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <IconButton
                  id="notifications"
                  onClick={() => {
                    if (notificationsCount > 0) {
                      supabase
                        .from("appNotifications")
                        .update({ unread: false })
                        .eq("unread", true)
                        .then((result) => {
                          if (result.error) {
                            enqueueSnackbar("Fehler: " + result.error.message, {
                              variant: "error",
                              anchorOrigin: {
                                vertical: "top",
                                horizontal: "center",
                              },
                            });
                          }
                        });
                    }
                    setShowNotificationsDialog(true);
                  }}
                  sx={{ p: 0 }}
                >
                  <Badge badgeContent={notificationsCount} color="info">
                    <NotificationsIcon color="secondary" />
                  </Badge>
                </IconButton>
                <IconButton
                  id="logout"
                  onClick={handleOpenUserMenu}
                  sx={{ p: 0 }}
                >
                  {avatarUrl ? (
                    <Avatar
                      variant="rounded"
                      src={avatarUrl}
                      sx={{
                        color: (theme) => theme.palette.primary.main,
                        bgcolor: (theme) => theme.palette.background.default,
                      }}
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{
                        color: (theme) => theme.palette.primary.main,
                        bgcolor: (theme) => theme.palette.background.default,
                      }}
                    />
                  )}
                </IconButton>
                <Menu
                  sx={{ mt: "45px" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem
                    id="account"
                    onClick={() => {
                      handleCloseUserMenu();
                      navigate("/account");
                    }}
                  >
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    Mein Account
                  </MenuItem>
                  <MenuItem
                    id="clients"
                    onClick={() => {
                      handleCloseUserMenu();
                      navigate("/clients");
                    }}
                  >
                    <ListItemIcon>
                      <CorporateFareIcon fontSize="small" />
                    </ListItemIcon>
                    Meine Mandanten
                  </MenuItem>
                  <MenuItem
                    id="support"
                    onClick={() => {
                      handleCloseUserMenu();
                      window.open(`${ssoBaseUrl}support`, "_blank");
                    }}
                  >
                    <ListItemIcon>
                      <SupportIcon fontSize="small" />
                    </ListItemIcon>
                    Support
                  </MenuItem>
                  <MenuItem id="logout" onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </Stack>
              <Dialog
                open={showNotificationsDialog}
                onClose={() => setShowNotificationsDialog(false)}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>Benachrichtigungen</DialogTitle>
                <DialogContent>
                  {notifications.length === 0 ? (
                    <List>
                      <ListItem>
                        <ListItemAvatar>
                          <InboxIcon color="primary" />
                        </ListItemAvatar>
                        <ListItemText
                          primary="Keine Benachrichtungen"
                          secondary="Es liegen keine Benachrichtigungen vor."
                        />
                      </ListItem>
                    </List>
                  ) : (
                    <List>
                      {notifications.map((row, index) => {
                        return (
                          <React.Fragment key={index}>
                            <ListItem
                              secondaryAction={
                                <React.Fragment>
                                  <Tooltip title="Löschen">
                                    <IconButton
                                      edge="end"
                                      aria-label="delete"
                                      onClick={() => {
                                        supabase
                                          .from("appNotifications")
                                          .delete()
                                          .eq("id", row.id)
                                          .then(({ error }) => {
                                            if (error) {
                                              enqueueSnackbar(
                                                "Fehler: " + error.message,
                                                {
                                                  variant: "error",
                                                  anchorOrigin: {
                                                    vertical: "top",
                                                    horizontal: "center",
                                                  },
                                                }
                                              );
                                            }
                                          });
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </React.Fragment>
                              }
                            >
                              <ListItemAvatar>
                                <AnnouncementIcon color="primary" />
                              </ListItemAvatar>
                              <Box flexGrow={1}>
                                <Typography variant="body1">
                                  {row.title}
                                </Typography>
                                <Typography variant="body2">
                                  {row.content}
                                </Typography>
                                <Stack
                                  sx={{ mt: 1 }}
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Box flexGrow={1}>
                                    <Button
                                      disabled={!row.action}
                                      variant="outlined"
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        navigate(row.action);
                                        setShowNotificationsDialog(false);
                                      }}
                                    >
                                      Details anzeigen
                                    </Button>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption">
                                      Vom{" "}
                                      {parseISO(
                                        row.created_at
                                      ).toLocaleString()}{" "}
                                      Uhr
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Box>
                            </ListItem>
                            <Divider variant="inset" component="li" />
                          </React.Fragment>
                        );
                      })}
                    </List>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowNotificationsDialog(false)}>
                    Zurück
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
